import { passkeyClient } from "@better-auth/passkey/client";
import { adminExtendClient } from "auth/plugins/admin/client";
import { sitesExtendClient } from "auth/plugins/sites/client";
import { passkeyExtendClient } from "auth/plugins/passkey/client";
import { twoFactorExtendClient } from "auth/plugins/two-factor/client";
import { userOrganizationsClient } from "auth/plugins/user-organizations.client";
import {
  adminClient,
  jwtClient,
  organizationClient,
  twoFactorClient,
} from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

// Same-origin: Caddy (and the dev vite proxy) forward `/auth/*` to the
// auth service, so better-auth can resolve the URL from the current origin.
const baseURL = typeof window !== "undefined" ? window.location.origin : "";

// Store JWT token in memory
let _jwtToken: string | null = null;
const _tokenSubscribers = new Set<(token: string | null) => void>();

/**
 * Get the current JWT token
 */
export function getJwtToken(): string | null {
  return _jwtToken;
}

/**
 * Set the JWT token. Notifies subscribers when the value actually changes.
 */
export function setJwtToken(token: string | null) {
  if (_jwtToken === token) return;
  _jwtToken = token;
  for (const cb of _tokenSubscribers) cb(token);
}

/**
 * Subscribe to JWT token changes. Returns an unsubscribe function.
 * Used by the auth store to refresh JWT-derived claims (e.g. siteClaims)
 * when the token rotates without the session object changing.
 */
export function subscribeJwtToken(
  cb: (token: string | null) => void,
): () => void {
  _tokenSubscribers.add(cb);
  return () => {
    _tokenSubscribers.delete(cb);
  };
}

// Create the auth client
const client = createAuthClient({
  /** The base URL of the server */
  baseURL,
  /** Match server route prefix */
  basePath: "/auth",
  plugins: [
    adminClient(),
    adminExtendClient(), // Custom admin endpoints (listOrganizations, listUsersDetailed)
    jwtClient(), // Added JWT client plugin to enable JWT extraction
    organizationClient(), // Assuming you have an organization client plugin
    userOrganizationsClient(),
    sitesExtendClient(),
    passkeyClient(), // WebAuthn sign-in and passkey management
    passkeyExtendClient(), // Last-used time, which the stock plugin does not record
    twoFactorClient(), // Authenticator-app 2FA (TOTP) and backup codes
    twoFactorExtendClient(), // Remaining recovery-code count
  ],
});

export const {
  signIn,
  signUp,
  useSession,
  getSession,
  signOut,
  admin,
  adminExtend,
  resetPassword,
  // better-auth renamed the password-reset request endpoint to
  // `/request-password-reset` (the old `forgetPassword`/`/forget-password`
  // route was removed). The client proxy kebab-cases the method name, so we
  // must call `requestPasswordReset` to hit the route the server serves —
  // calling `forgetPassword` POSTs to /auth/forget-password and 404s.
  requestPasswordReset,
  organization,
  useActiveOrganization,
  verifyEmail,
  sites,
  passkey,
  passkeyExtend,
  useListPasskeys,
  twoFactor,
  twoFactorExtend,
} = client;

export { client as authClient };

// Simple in-memory cache for session data
let _cachedSession: { data: unknown; expiry: number } | null = null;

/**
 * Clear the in-memory session cache so the next getCachedSession()
 * call hits the network. Used when a 401 indicates the cached
 * session/token is stale.
 */
export function clearSessionCache() {
  _cachedSession = null;
}

/**
 * Retrieves the session, using an in-memory cache to avoid redundant network calls.
 * The cache expires after a configurable TTL (default 5 minutes) or when the session data includes an `expiresAt` timestamp.
 * Also extracts and stores the JWT token from the response headers.
 */
export async function getCachedSession(): Promise<
  ReturnType<typeof getSession>
> {
  const now = Date.now();
  if (_cachedSession && _cachedSession.expiry > now) {
    return { data: _cachedSession.data } as ReturnType<typeof getSession>;
  }

  // Call getSession with fetchOptions to extract JWT from headers
  const result = await getSession({
    fetchOptions: {
      onSuccess: (ctx: { response: Response }) => {
        // Extract JWT from the 'set-auth-jwt' header
        const jwtToken = ctx.response.headers.get("set-auth-jwt");
        if (jwtToken) {
          setJwtToken(jwtToken);
          console.log("JWT token extracted from session");
        }
      },
    },
  });

  if (!result.error && result.data) {
    let ttl = 5 * 60 * 1000; // default 5 minutes
    // Define a type that may include expiresAt
    type SessionData = typeof result.data & { expiresAt?: string };
    const data = result.data as SessionData;
    if (data.expiresAt) {
      const expiresAt = new Date(data.expiresAt).getTime();
      ttl = Math.max(0, expiresAt - now);
    }
    _cachedSession = { data: result.data, expiry: now + ttl };
  }
  return result;
}

/**
 * Enhanced sign-in function that extracts JWT token from response headers
 */
export async function signInWithJwt(
  credentials: Parameters<typeof signIn.email>[0],
  callbackOptions?: Parameters<typeof signIn.email>[1],
) {
  return signIn.email(credentials, {
    ...callbackOptions,
    onSuccess: (ctx) => {
      // Extract JWT from response headers
      const jwtToken = ctx.response.headers.get("set-auth-jwt");
      if (jwtToken) {
        setJwtToken(jwtToken);
        console.log("JWT token extracted from sign-in");
      }
      // Call original onSuccess if provided
      callbackOptions?.onSuccess?.(ctx);
    },
  });
}

/**
 * True when this browser can do WebAuthn at all. Used to hide passkey UI
 * rather than offer a button that can only fail.
 */
export function supportsPasskeys(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window;
}

/**
 * Sign in with a passkey.
 *
 * Unlike password sign-in there is no JWT to pull off this response — the jwt
 * plugin only attaches `set-auth-jwt` to `/get-session`. The dashboard route
 * loader calls `getCachedSession()` on entry, which is where the token and
 * siteClaims actually arrive. Clearing the cache first makes sure that call
 * hits the network instead of reusing a pre-sign-in entry.
 */
export async function signInWithPasskey(
  options?: Parameters<typeof signIn.passkey>[0],
) {
  const result = await signIn.passkey(options);
  if (!result?.error) {
    clearSessionCache();
  }
  return result;
}

/**
 * Enhanced sign-out function that clears JWT token
 */
export async function signOutWithJwt(options?: Parameters<typeof signOut>[0]) {
  setJwtToken(null);
  _cachedSession = null;
  return signOut(options);
}
